import * as z from 'zod';
import { env } from '@noor/config';
import { readAuthToken, refreshSessionTokens } from '@noor/api';
import type { LegacyPoint } from '../model/schema';

/**
 * Road-snapped route between the order's stops, for the polyline the map draws.
 *
 * Served by a SELF-HOSTED OpenRouteService (`routing.noor.uz`), not the Noor API
 * and not Yandex — the old panel had a dedicated axios instance
 * (`requestRoute`) pointed at it for exactly this call. It therefore does NOT
 * go through `legacyHttp`: different host, different auth, and no `x-locale`.
 * The `X-Platform: web` marker is still attached literally, like the shared
 * clients do, so the backend sees one platform story.
 *
 * `VITE_ROUTE_API_URL` is already declared in the shared env schema (it was
 * carried over from cpanel). Empty disables route drawing, which is the
 * honest degradation — a straight line between stops would misrepresent the
 * distance the courier actually drives.
 */

/** GeoJSON `LineString` coordinates: `[[long, lat], …]`. */
const routeResponseSchema = z.object({
  features: z
    .array(
      z.object({
        geometry: z.object({
          coordinates: z.array(z.tuple([z.number(), z.number()])).default([]),
        }),
      }),
    )
    .default([]),
});

export type LegacyRoutePath = [number, number][];

/**
 * Requests the driving route through `points` in order. Returns `null` when
 * routing is unconfigured, when fewer than two points are given, or when the
 * service fails — the map then simply draws no line rather than a wrong one.
 *
 * On a 401 the shared session is renewed and the lookup retried ONCE with the
 * fresh token — the same refresh-and-retry the http clients run, done
 * manually because this bare fetch keeps its dev-token fallback and can't go
 * through those interceptors. Without it, every expiry window killed the
 * polyline with the rotated token until the next edit re-fired the lookup.
 * A failed renewal stays silent (no login bounce): routing is decoration.
 */
export async function fetchLegacyRoute(
  points: LegacyPoint[],
): Promise<LegacyRoutePath | null> {
  const base = env.VITE_ROUTE_API_URL;
  if (!base || points.length < 2) return null;

  // The route service is token-gated (a bare request answers 401 "Missing
  // token"). It accepts the merchant's own session token, which is what
  // production uses.
  //
  // The old panel additionally shipped a HARDCODED dev JWT and sent it on
  // every non-production host. That is not reproduced — an embedded credential
  // in client source is a liability, and that one is now in every build it
  // ever shipped. `VITE_ROUTE_API_DEV_TOKEN` serves the same purpose without
  // committing the secret: set it in a gitignored `.env.local` to draw routes
  // locally, where the mock session's token is not one the service accepts.
  //
  // The session token wins where there is one, so this can never override a
  // real merchant's credentials in production.
  // An EMPTY stored token must fall through to the dev one, not be treated as
  // a session — hence the explicit emptiness check rather than `??`.
  const sessionToken = readAuthToken();
  const token =
    sessionToken === null || sessionToken === ''
      ? env.VITE_ROUTE_API_DEV_TOKEN
      : sessionToken;
  if (!token) return null;

  try {
    const response = await postRoute(base, token, points);
    if (response.status === 401) {
      // Expired at send time — renew through the app-wide single flight
      // (a racing eval's refresh is joined, not duplicated) and retry once.
      const renewed = await refreshSessionTokens();
      if (renewed === null) return null;
      return await readRoute(await postRoute(base, renewed, points));
    }
    return await readRoute(response);
  } catch {
    // Routing is decoration: a failure must never block placing an order.
    return null;
  }
}

/** One routing lookup against `base` with an explicit bearer token. */
function postRoute(
  base: string,
  token: string,
  points: LegacyPoint[],
): Promise<Response> {
  return fetch(`${base.replace(/\/$/, '')}/ors/v2/directions/driving-car/geojson`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      // Same marker the shared clients attach (this bare fetch keeps its
      // dev-token fallback, so it can't go through those interceptors).
      'X-Platform': 'web',
    },
    body: JSON.stringify({
      coordinates: points.map((p) => [p.long, p.lat]),
      // Transcribed from the old `createOrUpdateMultiRoute` payload.
      extra_info: ['surface', 'steepness', 'waytype'],
      language: 'en',
      units: 'm',
      preference: 'recommended',
    }),
  });
}

/** Non-OK or malformed answers yield no line rather than a wrong one. */
async function readRoute(response: Response): Promise<LegacyRoutePath | null> {
  if (!response.ok) return null;

  const parsed = routeResponseSchema.safeParse(await response.json());
  if (!parsed.success) return null;

  const coordinates = parsed.data.features[0]?.geometry.coordinates ?? [];
  return coordinates.length > 0 ? coordinates : null;
}
